import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Text } from '../ui/Text';

interface JoinScreenProps {
  gameId: string;
  playerName: string;
  connectionStatus: string;
  onJoinGame: (gameId: string, playerName: string) => void;
}

const JoinScreen: React.FC<JoinScreenProps> = ({ gameId: initialGameId, playerName: initialPlayerName, connectionStatus, onJoinGame }) => {
  const { theme } = useTheme();
  const [gameId, setGameId] = useState(initialGameId);
  const [playerName, setPlayerName] = useState(initialPlayerName);
  const playerNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setGameId(initialGameId);
    if (initialGameId) {
      playerNameInputRef.current?.focus();
    }
  }, [initialGameId]);

  useEffect(() => {
    setPlayerName(initialPlayerName);
  }, [initialPlayerName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (gameId.trim() && playerName.trim()) {
      onJoinGame(gameId.trim(), playerName.trim());
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 animate-[fadeIn_0.3s_ease-in]">
      <Card
        variant="glass"
        elevation="xl"
        padding="lg"
        className="max-w-md w-full transform hover:scale-105 transition-transform duration-300"
      >
        <div className="text-center mb-8">
          <Text
            as="h1"
            size="4xl"
            weight="bold"
            align="center"
            className="mb-2 animate-pulse"
            style={{
              background: theme.colors.primary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            QuizCrunch
          </Text>
          <div className="text-2xl">🧠💥</div>
          <Text as="h2" size="2xl" weight="bold" color="primary" align="center" className="mt-4">
            Ready to Play?
          </Text>
          <Text size="base" color="onSurfaceVariant" align="center" className="mt-2">
            Enter your game details below!
          </Text>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl z-10">🎮</div>
            <input
              type="text"
              value={gameId}
              onChange={(e) => setGameId(e.target.value)}
              className="w-full pl-12 pr-4 py-4 text-lg font-bold rounded-2xl focus:outline-none focus:ring-4 transition-all duration-300 min-h-[48px]"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: `2px solid ${theme.colors.outline}`,
                color: '#000',
              }}
              placeholder="Game ID"
              maxLength={20}
            />
          </div>

          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl z-10">👤</div>
            <input
              ref={playerNameInputRef}
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="w-full pl-12 pr-4 py-4 text-lg font-bold rounded-2xl focus:outline-none focus:ring-4 transition-all duration-300 min-h-[48px]"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: `2px solid ${theme.colors.outline}`,
                color: '#000',
              }}
              placeholder="Your awesome name"
              maxLength={20}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            icon="🚀"
          >
            Join the Fun!
          </Button>

          {connectionStatus && (
            <Text size="base" weight="bold" color="primary" align="center">
              {connectionStatus}
            </Text>
          )}
        </form>
      </Card>
    </div>
  );
};

export default JoinScreen;